// Physical Inventory Count (docs/PHYSICAL_INVENTORY_COUNT_MODULE.md) — lets any factory/QA user
// reconcile ingredient stock against a real physical count, whenever they want. Ingredients that
// have ever been received with a lot code expand into one editable row per lot (+ an "Unassigned"
// row when some of the ingredient's stock isn't attributable to a known lot) so the resulting
// adjustment stays tied to the correct lot for traceability; lot-less ingredients stay a single row.
import React, { useState, useEffect, useMemo } from 'react';
import ingredientInventoryService from '../services/ingredientInventoryService';

const today = () => new Date().toISOString().split('T')[0];

const ADJ_TYPE_LABELS = {
  count: 'Stock Count',
  waste: 'Waste',
  damage: 'Damage',
  sample: 'Sample',
  correction: 'Correction',
  resale: 'Resale',
  other: 'Other',
};

// Flattens the draft sheet into one editable row per (ingredient, lot | unassigned | lot-less).
function buildRows(sheet) {
  const rows = [];
  for (const ing of sheet) {
    const subRows = [];
    if (ing.lots.length === 0) {
      subRows.push({ lotCode: null, label: null, systemQty: ing.totalStockKg });
    } else {
      for (const lot of ing.lots) {
        subRows.push({ lotCode: lot.lotCode, label: `Lot ${lot.lotCode}`, systemQty: lot.remainingKg });
      }
      if (ing.unassignedKg != null) {
        subRows.push({ lotCode: null, label: 'Unassigned (no lot code)', systemQty: ing.unassignedKg });
      }
    }
    subRows.forEach((sr, idx) => {
      rows.push({
        key: `${ing.ingredientId}-${sr.lotCode || 'none'}-${idx}`,
        ingredientId: ing.ingredientId,
        ingredientName: ing.name,
        rawMaterialCode: ing.rawMaterialCode,
        isFirstOfIngredient: idx === 0,
        groupSize: subRows.length,
        lotCode: sr.lotCode,
        label: sr.label,
        systemQty: sr.systemQty,
        countedQty: '',
        adjustmentType: 'count',
        notes: '',
      });
    });
  }
  return rows;
}

export default function IngredientPhysicalCountModal({ onClose, onCompleted }) {
  const [phase, setPhase] = useState('loading'); // loading | review | submitting | done | error
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [countedAt, setCountedAt] = useState(today());
  const [sessionNotes, setSessionNotes] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    ingredientInventoryService.getPhysicalCountDraftSheet()
      .then(sheet => {
        setRows(buildRows(sheet));
        setPhase('review');
      })
      .catch(e => {
        setError(e?.response?.data?.message || e.message || 'Failed to load ingredients.');
        setPhase('error');
      });
  }, []);

  const setRow = (key, field, value) => {
    setRows(prev => prev.map(r => (r.key === key ? { ...r, [field]: value } : r)));
  };

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    const matchingIngredientIds = new Set(
      rows.filter(r => r.ingredientName.toLowerCase().includes(q) || (r.rawMaterialCode || '').toLowerCase().includes(q))
        .map(r => r.ingredientId)
    );
    return rows.filter(r => matchingIngredientIds.has(r.ingredientId));
  }, [rows, search]);

  const deltaFor = (row) => {
    if (row.countedQty === '') return null;
    const counted = parseFloat(row.countedQty);
    if (isNaN(counted)) return null;
    return Math.round((counted - row.systemQty) * 1000) / 1000;
  };

  const countedRowCount = rows.filter(r => r.countedQty !== '').length;

  const handleSubmit = async () => {
    const lines = rows
      .filter(r => r.countedQty !== '')
      .map(r => ({
        ingredientId: r.ingredientId,
        lotCode: r.lotCode,
        countedKg: parseFloat(r.countedQty),
        adjustmentType: r.adjustmentType,
        notes: r.notes || null,
      }));
    if (lines.length === 0) {
      setError('Enter at least one counted quantity.');
      return;
    }
    setError('');
    setPhase('submitting');
    try {
      const res = await ingredientInventoryService.submitPhysicalCount({
        countedAt,
        notes: sessionNotes || null,
        lines,
      });
      setResult(res);
      setPhase('done');
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to submit the count.');
      setPhase('review');
    }
  };

  const handleClose = () => {
    if (phase === 'done') onCompleted();
    onClose();
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">📋 Physical Inventory Count</h5>
            <button type="button" className="btn-close" onClick={handleClose} disabled={phase === 'submitting'} />
          </div>
          <div className="modal-body">
            {phase === 'loading' && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" />
              </div>
            )}

            {phase === 'error' && <div className="alert alert-danger">{error}</div>}

            {(phase === 'review' || phase === 'submitting') && (
              <>
                {error && <div className="alert alert-danger py-2">{error}</div>}
                <div className="row g-3 mb-3">
                  <div className="col-md-3">
                    <label className="form-label">Count Date</label>
                    <input type="date" className="form-control" value={countedAt} onChange={e => setCountedAt(e.target.value)} />
                  </div>
                  <div className="col-md-5">
                    <label className="form-label">Notes (optional)</label>
                    <input type="text" className="form-control" value={sessionNotes}
                      onChange={e => setSessionNotes(e.target.value)} placeholder="e.g. Monthly count — October" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Filter</label>
                    <input type="text" className="form-control" value={search}
                      onChange={e => setSearch(e.target.value)} placeholder="Search ingredient…" />
                  </div>
                </div>

                <div className="text-muted small mb-2">
                  Enter the counted quantity only for items you're checking this time — everything
                  else is left as-is. {countedRowCount > 0 && <strong>{countedRowCount} counted so far.</strong>}
                </div>

                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Ingredient / Lot</th>
                      <th style={{ width: 110 }}>System (kg)</th>
                      <th style={{ width: 120 }}>Counted (kg)</th>
                      <th style={{ width: 90 }}>Delta</th>
                      <th style={{ width: 150 }}>Reason</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map(row => {
                      const delta = deltaFor(row);
                      return (
                        <tr key={row.key} className={delta != null && Math.abs(delta) >= 0.001 ? 'table-warning' : ''}>
                          <td>
                            {row.isFirstOfIngredient && row.groupSize > 1 && (
                              <div className="fw-semibold">
                                {row.ingredientName}
                                {row.rawMaterialCode && <span className="ms-1 badge bg-secondary">{row.rawMaterialCode}</span>}
                              </div>
                            )}
                            {row.groupSize === 1 ? (
                              <>
                                {row.ingredientName}
                                {row.rawMaterialCode && <span className="ms-1 badge bg-secondary">{row.rawMaterialCode}</span>}
                              </>
                            ) : (
                              <span className="text-muted ms-2" style={{ fontSize: 12 }}>↳ {row.label}</span>
                            )}
                          </td>
                          <td>{row.systemQty.toFixed(2)}</td>
                          <td>
                            <input type="number" className="form-control form-control-sm" value={row.countedQty}
                              onChange={e => setRow(row.key, 'countedQty', e.target.value)} />
                          </td>
                          <td className={delta == null ? '' : delta > 0 ? 'text-success' : delta < 0 ? 'text-danger' : 'text-muted'}>
                            {delta == null ? '—' : `${delta > 0 ? '+' : ''}${delta.toFixed(2)}`}
                          </td>
                          <td>
                            {delta != null && Math.abs(delta) >= 0.001 ? (
                              <select className="form-select form-select-sm" value={row.adjustmentType}
                                onChange={e => setRow(row.key, 'adjustmentType', e.target.value)}>
                                {Object.entries(ADJ_TYPE_LABELS).map(([val, label]) => (
                                  <option key={val} value={val}>{label}</option>
                                ))}
                              </select>
                            ) : <span className="text-muted">—</span>}
                          </td>
                          <td>
                            <input type="text" className="form-control form-control-sm" value={row.notes}
                              onChange={e => setRow(row.key, 'notes', e.target.value)} placeholder="optional" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}

            {phase === 'done' && (
              <div className="py-3">
                <div className="text-center mb-3">
                  <div className="mb-2" style={{ fontSize: 32 }}>✅</div>
                  <div className="fw-semibold">
                    Counted {result?.lineCount || 0} item{result?.lineCount === 1 ? '' : 's'} —
                    {' '}{result?.adjustmentCount || 0} adjustment{result?.adjustmentCount === 1 ? '' : 's'} created.
                  </div>
                </div>
                {(result?.lines || []).filter(l => l.adjustmentId).length > 0 && (
                  <table className="table table-sm">
                    <thead><tr><th>Ingredient</th><th>Lot</th><th>Delta (kg)</th></tr></thead>
                    <tbody>
                      {result.lines.filter(l => l.adjustmentId).map(l => {
                        const row = rows.find(r => r.ingredientId === l.ingredientId && r.lotCode === l.lotCode);
                        return (
                          <tr key={l.id}>
                            <td>{row?.ingredientName || l.ingredientId}</td>
                            <td>{l.lotCode || '—'}</td>
                            <td className={Number(l.deltaKg) >= 0 ? 'text-success' : 'text-danger'}>
                              {Number(l.deltaKg) > 0 ? '+' : ''}{Number(l.deltaKg).toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            {phase !== 'done' && (
              <button className="btn btn-outline-secondary" onClick={handleClose} disabled={phase === 'submitting'}>Cancel</button>
            )}
            {(phase === 'review' || phase === 'submitting') && (
              <button className="btn btn-primary" onClick={handleSubmit} disabled={phase === 'submitting' || countedRowCount === 0}>
                {phase === 'submitting' ? <><span className="spinner-border spinner-border-sm me-2" />Submitting…</> : `Complete Count (${countedRowCount})`}
              </button>
            )}
            {phase === 'done' && (
              <button className="btn btn-primary" onClick={handleClose}>Done</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
